import { DraftShape, Shape, Storey } from '@shared/models/design/storey.model';
import { Point2D } from '@shared/models/geometry.model';
import {
  doShapesIntersect,
  isSolidPairAllowedByCutoutHole,
} from '@shared/helpers/design/shape.helpers';
import {
  getStoreyBelow,
  getStoreyById,
} from '@shared/helpers/design/storey.helpers';
import { designGuards } from '@shared/helpers/design/design-typeguards.helpers';
import {
  doPolygonsIntersect,
  isEqualPoints,
  toPoint2D,
} from '@shared/helpers/geometry/geometry.helpers';
import {
  findEnclosingSolidShape,
  findSmallestContainingCutout,
  getCutoutsEnclosedBySolid,
} from '@shared/helpers/design/cutout.helpers';

const OVERLAP_CLAMP_BINARY_STEPS = 20;
const MIN_OVERLAP_PROGRESS_SQ = 0.0004;
const MIN_EDGE_LENGTH = 0.001;

export type ShapeTranslationTarget = {
  storeyId: Storey['id'];
  storeyLevel: number;
};

export type ShapeTranslationTargets = {
  chain: ShapeTranslationTarget[];
  intersections: Record<Storey['id'], Shape['id'][]>;
};

type ResolveShapeTranslationTargetsParams = {
  storeys: Storey[];
  shapeId: Shape['id'];
  baseShape: Shape | DraftShape;
  originPoints: Point2D[];
  baseStoreyIndex: number;
};

/**
 * Ensures cutouts on storeys above the drag storey move whenever a translated solid that
 * encloses them moves. Footprint intersection against the ground shape alone can miss holes
 * (e.g. geometry edge cases or cutouts defined only relative to the upper solid).
 *
 * @param storeys Full storey stack
 * @param draggedShapeId Shape id carried through the vertical chain (ground drag target)
 * @param baseStoreyIndex Index of the storey where the drag started
 * @param chain Vertical chain entries from resolveShapeTranslationTargets
 * @param intersections Mutable map of extra shape ids per storey (updated in place)
 */
const appendCutoutsInsideMovedSolidsOnUpperStoreys = (
  storeys: Storey[],
  draggedShapeId: Shape['id'],
  baseStoreyIndex: number,
  chain: ShapeTranslationTarget[],
  intersections: Record<Storey['id'], Shape['id'][]>,
): void => {
  const movedIdsByStorey = new globalThis.Map<Storey['id'], Set<Shape['id']>>();

  const addMoved = (storeyId: Storey['id'], id: Shape['id']): void => {
    const set = movedIdsByStorey.get(storeyId) ?? new Set<Shape['id']>();
    set.add(id);
    movedIdsByStorey.set(storeyId, set);
  };

  for (const t of chain) {
    addMoved(t.storeyId, draggedShapeId);
  }
  for (const [storeyId, ids] of Object.entries(intersections)) {
    for (const id of ids) {
      addMoved(storeyId, id);
    }
  }

  for (let index = baseStoreyIndex + 1; index < storeys.length; index += 1) {
    const storey = storeys[index];
    if (!storey?.shapes?.length) continue;

    const movedIds = movedIdsByStorey.get(storey.id);
    if (!movedIds?.size) continue;

    const movedSolids = storey.shapes.filter(
      (s) => s.cutout !== true && movedIds.has(s.id),
    );
    if (!movedSolids.length) continue;

    const existing = new Set<Shape['id']>(intersections[storey.id] ?? []);
    let added = false;
    for (const candidate of storey.shapes) {
      if (candidate.cutout !== true) continue;
      if (existing.has(candidate.id)) continue;
      // The hole follows the solid that encloses it, not a courtyard house inside it.
      const enclosed = movedSolids.some(
        (solid) => findEnclosingSolidShape(storey, candidate)?.id === solid.id,
      );
      if (!enclosed) continue;
      existing.add(candidate.id);
      added = true;
    }
    if (added) {
      intersections[storey.id] = Array.from(existing);
    }
  }
};

/**
 * Extends {@link intersections} so every storey above the drag also translates shapes that
 * touch any shape already moving on the storey directly below. Direct intersection against the
 * ground shape alone misses "two steps up" footprints (DEV-3036).
 *
 * @param storeys Full storey stack
 * @param draggedShapeId Shape id carried through the vertical chain when present
 * @param baseStoreyIndex Index of the storey where the drag started
 * @param chain Vertical chain from {@link resolveShapeTranslationTargets}
 * @param intersections Mutable map of shape ids per storey (updated in place)
 */
const propagateShapeTranslationIntersectionsUpward = (
  storeys: Storey[],
  draggedShapeId: Shape['id'],
  baseStoreyIndex: number,
  chain: ShapeTranslationTarget[],
  intersections: Record<Storey['id'], Shape['id'][]>,
): void => {
  const storeyIdsInChain = new Set(chain.map((t) => t.storeyId));

  /** Shapes on a storey that will translate: chained solid plus intersection picks. */
  const getMovingShapesOnStorey = (storeyIndex: number): Shape[] => {
    const storey = storeys[storeyIndex];
    if (!storey?.shapes?.length) return [];

    const ids = new Set<Shape['id']>();
    if (storeyIdsInChain.has(storey.id)) ids.add(draggedShapeId);
    for (const id of intersections[storey.id] ?? []) ids.add(id);

    return storey.shapes.filter((s) => ids.has(s.id));
  };

  for (let index = baseStoreyIndex + 1; index < storeys.length; index += 1) {
    const moversBelow = getMovingShapesOnStorey(index - 1);
    if (!moversBelow.length) continue;

    const storey = storeys[index];
    if (!storey?.shapes?.length) continue;

    const existing = new Set<Shape['id']>(intersections[storey.id] ?? []);
    let added = false;
    for (const candidate of storey.shapes) {
      if (!candidate.points?.length) continue;
      // Chained footprint is translated via chain entries, not intersection ids.
      const carriedByChain =
        candidate.id === draggedShapeId && storeyIdsInChain.has(storey.id);
      if (carriedByChain) continue;
      if (existing.has(candidate.id)) continue;
      const touchesMovedBelow = moversBelow.some((below) =>
        shapeFollowsTranslation(below, candidate, storey, storeys[index - 1]),
      );
      if (!touchesMovedBelow) continue;
      existing.add(candidate.id);
      added = true;
    }
    if (added) {
      intersections[storey.id] = Array.from(existing);
    }
  }
};

export const resolveShapeTranslationTargets = ({
  storeys,
  shapeId,
  baseShape,
  originPoints,
  baseStoreyIndex,
}: ResolveShapeTranslationTargetsParams): ShapeTranslationTargets => {
  const chain: ShapeTranslationTarget[] = [];
  const intersections: Record<Storey['id'], Shape['id'][]> = {};
  const baseStorey = storeys[baseStoreyIndex];
  if (!baseStorey) return { chain, intersections };

  if (baseShape.cutout) {
    // Cutout drag should only affect cutouts in regular storeys above.
    chain.push({ storeyId: baseStorey.id, storeyLevel: baseStoreyIndex });
    for (let index = baseStoreyIndex + 1; index < storeys.length; index += 1) {
      const candidateStorey = storeys[index];
      if (!candidateStorey?.shapes?.length) continue;
      if (designGuards.is.mezzanineStorey(candidateStorey)) continue;

      const linkedCutouts = candidateStorey.shapes.filter((candidate) => {
        if (candidate.cutout !== true) return false;
        if (!candidate.points?.length) return false;
        return doShapesIntersect(baseShape as Shape, candidate);
      });
      if (!linkedCutouts.length) continue;
      intersections[candidateStorey.id] = linkedCutouts.map(
        (candidate) => candidate.id,
      );
    }
    return { chain, intersections };
  }

  for (let index = baseStoreyIndex; index < storeys.length; index += 1) {
    const candidateStorey = storeys[index];
    if (!candidateStorey) break;
    const candidateShape = candidateStorey.shapes.find(
      (shape) => shape.id === shapeId,
    );
    if (!candidateShape?.points?.length) break;
    if (candidateShape.points.length !== originPoints.length) break;
    const matches = candidateShape.points.every((point, pointIndex) => {
      const reference = originPoints[pointIndex];
      if (!reference) return false;
      return isEqualPoints(toPoint2D(point), reference);
    });
    if (!matches) break;
    chain.push({ storeyId: candidateStorey.id, storeyLevel: index });
  }

  for (let index = baseStoreyIndex + 1; index < storeys.length; index += 1) {
    const candidateStorey = storeys[index];
    if (!candidateStorey?.shapes?.length) continue;
    const intersectingShapes = candidateStorey.shapes.filter((candidate) => {
      if (!candidate?.points?.length) return false;
      return shapeFollowsTranslation(
        baseShape as Shape,
        candidate,
        candidateStorey,
        baseStorey,
      );
    });
    if (!intersectingShapes.length) continue;
    intersections[candidateStorey.id] = intersectingShapes.map(
      (candidate) => candidate.id,
    );
  }

  if (baseStorey && !baseShape.cutout && baseStorey.shapes.length > 1) {
    const linkedCutouts = getCutoutsEnclosedBySolid(
      baseStorey,
      baseShape as Shape,
    ).filter((candidate) => candidate.id !== shapeId);
    if (linkedCutouts.length > 0) {
      intersections[baseStorey.id] = [
        ...(intersections[baseStorey.id] ?? []),
        ...linkedCutouts.map((shape) => shape.id),
      ];
    }
  }

  propagateShapeTranslationIntersectionsUpward(
    storeys,
    shapeId,
    baseStoreyIndex,
    chain,
    intersections,
  );

  appendCutoutsInsideMovedSolidsOnUpperStoreys(
    storeys,
    shapeId,
    baseStoreyIndex,
    chain,
    intersections,
  );

  return { chain, intersections };
};

/**
 * Whether {@link candidate} should translate with {@link mover}.
 * A cutout follows only the solid that encloses it. A courtyard house stays
 * inside its hole and does not pull the cutout or the outer plate with it.
 */
const shapeFollowsTranslation = (
  mover: Shape,
  candidate: Shape,
  candidateStorey: Storey,
  moverStorey: Storey | undefined,
): boolean => {
  const moverIsInsideCutout =
    findSmallestContainingCutout(candidateStorey, mover) !== undefined ||
    findSmallestContainingCutout(moverStorey, mover) !== undefined;
  if (moverIsInsideCutout) return false;

  if (candidate.cutout === true) {
    return findEnclosingSolidShape(candidateStorey, candidate)?.id === mover.id;
  }
  if (isSolidPairAllowedByCutoutHole(mover, candidate, candidateStorey)) {
    return false;
  }
  return doShapesIntersect(mover, candidate);
};

type WholeBuildingTranslateOptions = {
  tool: 'build' | 'draw' | 'moveBuilding';
  isBuildingOutlineSelected: boolean;
};

/**
 * True when a drag should translate every shape on the selected building
 * by the same delta: the Move-building tool, or a building-outline selection.
 */
export const isWholeBuildingTranslate = (
  options: WholeBuildingTranslateOptions,
): boolean =>
  options.tool === 'moveBuilding' || options.isBuildingOutlineSelected;

/**
 * Translation targets that move every shape on every storey together.
 * Used when {@link isWholeBuildingTranslate} is true.
 */
export const buildWholeBuildingTranslationTargets = (
  storeys: Storey[],
): ShapeTranslationTargets => ({
  chain: storeys.map((storey, storeyLevel) => ({
    storeyId: storey.id,
    storeyLevel,
  })),
  intersections: Object.fromEntries(
    storeys.map((storey) => [
      storey.id,
      storey.shapes.map((shape) => shape.id),
    ]),
  ),
});

export type ClampStoreyOverlapDeltaParams = {
  storeys: Storey[];
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  delta: Point2D;
  /** When every storey moves by the same delta, relative overlap is unchanged. */
  isWholeBuildingTranslate: boolean;
};

/**
 * Clamp a drag so a non-ground solid still overlaps at least one shape on the
 * storey below. Ground floor and cutouts are unchanged.
 *
 * Whole-building translation must keep the requested delta: clamping against
 * floors that have not moved yet would tether the building to itself.
 */
export const clampStoreyOverlapDelta = ({
  storeys,
  storeyId,
  shapeId,
  delta,
  isWholeBuildingTranslate,
}: ClampStoreyOverlapDeltaParams): Point2D => {
  if (isWholeBuildingTranslate) return delta;
  const storey = getStoreyById(storeys, storeyId);
  if (!storey || storey.mezzanine) return delta;

  const storeyBelow = getStoreyBelow(storeys, storey);
  if (!storeyBelow?.shapes?.length) return delta;

  const shape = storey.shapes.find((candidate) => candidate.id === shapeId);
  if (!shape?.points?.length) return delta;
  if (shape.cutout === true) return delta;

  const [dx, dy] = delta;
  if (doesTranslatedShapeOverlapBelow(shape.points, dx, dy, storeyBelow)) {
    return delta;
  }

  const directDelta = binarySearchOverlapDelta(
    shape.points,
    dx,
    dy,
    storeyBelow,
  );
  const directDist =
    directDelta[0] * directDelta[0] + directDelta[1] * directDelta[1];
  if (directDist > MIN_OVERLAP_PROGRESS_SQ) return directDelta;

  return (
    slideOverlapTranslation(shape.points, dx, dy, storeyBelow) ?? directDelta
  );
};

/** True when translated points still intersect any shape on {@link storeyBelow}. */
const doesTranslatedShapeOverlapBelow = (
  points: readonly Point2D[],
  dx: number,
  dy: number,
  storeyBelow: Storey,
): boolean => {
  const translated = points.map(([x, y]): Point2D => [x + dx, y + dy]);
  return storeyBelow.shapes.some((belowShape) => {
    const belowPts = belowShape.points;
    if (!belowPts?.length) return false;
    return doPolygonsIntersect(translated, belowPts);
  });
};

/** Largest ratio of {@link dx}/{@link dy} that still overlaps the storey below. */
const binarySearchOverlapDelta = (
  points: readonly Point2D[],
  dx: number,
  dy: number,
  storeyBelow: Storey,
): Point2D => {
  let low = 0;
  let high = 1;
  for (let i = 0; i < OVERLAP_CLAMP_BINARY_STEPS; i += 1) {
    const mid = (low + high) / 2;
    if (
      doesTranslatedShapeOverlapBelow(points, dx * mid, dy * mid, storeyBelow)
    ) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return [dx * low, dy * low];
};

/** Slide along storey-below edges when the direct delta loses overlap immediately. */
const slideOverlapTranslation = (
  points: readonly Point2D[],
  dx: number,
  dy: number,
  storeyBelow: Storey,
): Point2D | undefined => {
  const dragLen = Math.sqrt(dx * dx + dy * dy);
  if (dragLen < MIN_EDGE_LENGTH) return undefined;

  let bestDelta: Point2D | undefined;
  let bestDist = 0;

  for (const belowShape of storeyBelow.shapes) {
    const pts = belowShape.points ?? [];
    if (pts.length < 2) continue;
    const total = pts.length;

    for (let i = 0; i < total; i += 1) {
      const start = pts[i];
      const end = pts[(i + 1) % total];
      if (!start || !end) continue;

      const edgeDx = end[0] - start[0];
      const edgeDy = end[1] - start[1];
      const edgeLen = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
      if (edgeLen < MIN_EDGE_LENGTH) continue;

      const dot = (dx * edgeDx + dy * edgeDy) / edgeLen;
      if (Math.abs(dot) < MIN_EDGE_LENGTH) continue;

      const candidate = binarySearchOverlapDelta(
        points,
        (edgeDx / edgeLen) * dot,
        (edgeDy / edgeLen) * dot,
        storeyBelow,
      );
      const dist = candidate[0] * candidate[0] + candidate[1] * candidate[1];
      if (dist > bestDist) {
        bestDist = dist;
        bestDelta = candidate;
      }
    }
  }

  if (bestDelta && bestDist > MIN_OVERLAP_PROGRESS_SQ) return bestDelta;
  return undefined;
};
